// src/pages/Login.js

import React, {
  useState,
  useEffect,
} from "react";

import { login } from "../services/auth";

export default function Login({
  onLogin,
}) {
  const [username, setUsername] =
    useState("");

  const [password, setPassword] =
    useState("");

  const [error, setError] =
    useState("");

  const [loading, setLoading] =
    useState(false);

  /* =========================
     LISTEN FOR SESSION EXPIRE
  ========================= */

  useEffect(() => {
    const handler = () => {
      setError(
        "Session expired. Please login again."
      );
    };

    window.addEventListener(
      "session-expired",
      handler
    );

    return () =>
      window.removeEventListener(
        "session-expired",
        handler
      );
  }, []);

  /* =========================
     LOGIN
  ========================= */

  const submit = async (e) => {
    e.preventDefault();

    if (loading) return;

    setError("");

    try {
      setLoading(true);

      const user = await login(
        username,
        password
      );

      onLogin?.(user);
    } catch (err) {
      console.error(
        "LOGIN ERROR:",
        err
      );

      setError(
        err.response?.data
          ?.message ||
          err.message ||
          "Invalid username or password"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* LEFT BRAND PANEL */}

      <div className="login-left">
        <div className="brand">
          <h1>Zaka-Pay</h1>

          <p>
            Smart. Secure.
          </p>
        </div>
      </div>

      {/* RIGHT LOGIN FORM */}

      <div className="login-right">
        <form
          className="login-card"
          onSubmit={submit}
        >
          <div className="login-header">
            <h2>
              Welcome Back
            </h2>

            <p>
              Sign in to continue
            </p>
          </div>

          {error && (
            <div className="login-error">
              {error}
            </div>
          )}

          <label className="login-label">
            Username
          </label>

          <input
            className="login-input"
            value={username}
            onChange={(e) =>
              setUsername(
                e.target.value
              )
            }
            required
          />

          <label className="login-label">
            Password
          </label>

          <input
            className="login-input"
            type="password"
            value={password}
            onChange={(e) =>
              setPassword(
                e.target.value
              )
            }
            required
          />

          <div className="login-options">
            <label>
              <input type="checkbox" />{" "}
              Remember me
            </label>
          </div>

          <button
            className="login-btn"
            disabled={loading}
          >
            {loading
              ? "Signing in..."
              : "Sign in"}
          </button>

          <div className="login-footer">
            ©{" "}
            {new Date().getFullYear()}{" "}
            Zaka-Pay
          </div>
        </form>
      </div>

      {/* STYLES */}

      <style>{`
        * {
          box-sizing: border-box;
        }

        body {
          margin: 0;
          padding: 0;
        }

        .login-container {
          display: flex;
          min-height: 100vh;
          font-family:
            Inter,
            system-ui,
            sans-serif;
          background: #f8fafc;
        }

        /* =====================================
           LEFT PANEL
        ===================================== */

        .login-left {
          flex: 1;

          background:
            linear-gradient(
              135deg,
              #5B4B8A,
              #3f3364
            );

          color: white;

          display: flex;
          align-items: center;
          justify-content: center;

          padding: 40px;
        }

        .brand {
          text-align: center;
          max-width: 360px;
        }

        .brand h1 {
          margin: 0 0 12px;

          font-size: 48px;
          font-weight: 800;
          letter-spacing: -1px;
        }

        .brand p {
          margin: 0;

          font-size: 18px;
          opacity: 0.9;
          line-height: 1.6;
        }

        /* =====================================
           RIGHT PANEL
        ===================================== */

        .login-right {
          flex: 1;

          display: flex;
          align-items: center;
          justify-content: center;

          padding: 40px;
          background: #f8fafc;
        }

        /* =====================================
           LOGIN CARD
        ===================================== */

        .login-card {
          width: 100%;
          max-width: 380px;

          background: white;

          padding: 36px;

          border-radius: 18px;

          border: 1px solid #e2e8f0;

          box-shadow:
            0 10px 40px
            rgba(15, 23, 42, 0.08);
        }

        /* =====================================
           HEADER
        ===================================== */

        .login-header {
          margin-bottom: 24px;
        }

        .login-header h2 {
          margin: 0 0 8px;

          color: #0f172a;

          font-size: 28px;
          font-weight: 700;
        }

        .login-header p {
          margin: 0;

          color: #64748b;
          font-size: 14px;
        }

        /* =====================================
           ERROR
        ===================================== */

        .login-error {
          background: #fef2f2;
          color: #b91c1c;

          padding: 12px;

          border-radius: 10px;

          margin-bottom: 16px;

          font-size: 13px;

          border: 1px solid #fecaca;
        }

        /* =====================================
           LABELS
        ===================================== */

        .login-label {
          display: block;

          margin-bottom: 6px;

          font-size: 13px;
          font-weight: 600;

          color: #334155;
        }

        /* =====================================
           INPUTS
        ===================================== */

        .login-input {
          width: 100%;

          padding: 12px 14px;

          margin-bottom: 16px;

          border-radius: 10px;

          border: 1px solid #cbd5e1;

          font-size: 14px;

          transition:
            border-color 0.2s,
            box-shadow 0.2s;
        }

        .login-input:focus {
          outline: none;

          border-color: #5B4B8A;

          box-shadow:
            0 0 0 3px
            rgba(91,75,138,0.15);
        }

        /* =====================================
           OPTIONS
        ===================================== */

        .login-options {
          display: flex;
          justify-content: space-between;
          align-items: center;

          margin-bottom: 18px;

          font-size: 13px;

          color: #475569;
        }

        /* =====================================
           BUTTON
        ===================================== */

        .login-btn {
          width: 100%;

          border: none;

          background: #5B4B8A;
          color: white;

          padding: 13px 18px;

          border-radius: 12px;

          font-size: 15px;
          font-weight: 700;

          cursor: pointer;

          transition:
            all 0.2s ease;

          box-shadow:
            0 4px 14px
            rgba(91,75,138,0.25);
        }

        .login-btn:hover {
          background: #4b3d74;

          transform:
            translateY(-1px);

          box-shadow:
            0 8px 20px
            rgba(91,75,138,0.35);
        }

        .login-btn:active {
          transform: scale(0.98);
        }

        .login-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }

        /* =====================================
           FOOTER
        ===================================== */

        .login-footer {
          margin-top: 18px;

          text-align: center;

          font-size: 12px;

          color: #94a3b8;
        }

        /* =====================================
           MOBILE
        ===================================== */

        @media (max-width: 768px) {
          .login-container {
            flex-direction: column;
          }

          .login-left {
            display: none;
          }

          .login-right {
            padding: 20px;
          }

          .login-card {
            max-width: 100%;
            padding: 28px;
          }
        }
      `}</style>
    </div>
  );
}